// BankAccountDetails.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase/firebase-config";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
deleteDoc,
  doc,
} from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import "./All_Customers";

/* ---------- Constants ---------- */
const INDIAN_BANKS = [
  "State Bank of India (SBI)",
  "HDFC Bank",
  "ICICI Bank",
  "Axis Bank",
  "Kotak Mahindra Bank",
  "Punjab National Bank",
  "Bank of Baroda",
  "Canara Bank",
  "Union Bank of India",
  "IDBI Bank",
  "Yes Bank",
  "IndusInd Bank",
  "Federal Bank",
  "Bank of India",
  "Central Bank of India",
  "UCO Bank",
  "Bank of Maharashtra",
  "Indian Bank",
  "IDFC FIRST Bank",
  "Other",
];

const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/; // e.g., HDFC0001234
const inMobile10Regex = /^[6-9]\d{9}$/; // 10-digit India mobile

/* ---------- Small Animated Bits (AI vibes) ---------- */
// Ambient sparkles
const Sparkles = () => {
  const dots = useMemo(
    () =>
      Array.from({ length: 18 }).map((_, i) => ({
        id: i,
        left: `${Math.random() * 92 + 4}%`,
        top: `${Math.random() * 60 + 4}%`,
        size: Math.random() * 6 + 3,
        dur: Math.random() * 4 + 3,
        delay: Math.random() * 2,
      })),
    []
  );
  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
      {dots.map((d) => (
        <motion.span
          key={d.id}
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: [-8, 8, -8], opacity: [0.1, 0.28, 0.1] }}
          transition={{ duration: d.dur, repeat: Infinity, delay: d.delay, ease: "easeInOut" }}
          style={{
            position: "absolute",
            left: d.left,
            top: d.top,
            width: d.size,
            height: d.size,
            borderRadius: 999,
            background:
              "radial-gradient(circle at 30% 30%, rgba(255,255,255,.9), rgba(255,255,255,.0) 60%)",
            filter: "blur(.6px)",
          }}
        />
      ))}
    </div>
  );
};

// Confetti burst
const Confetti = ({ show = false }) => {
  const pieces = useMemo(
    () =>
      Array.from({ length: 26 }).map((_, i) => ({
        id: i,
        x: (Math.random() - 0.5) * 240,
        y: (Math.random() - 0.5) * 60,
        r: Math.random() * 360,
        s: Math.random() * 8 + 4,
        d: Math.random() * 0.6 + 0.6,
      })),
    []
  );
  if (!show) return null;
  return (
    <div style={{ position: "fixed", top: "15%", left: "50%", zIndex: 9999, pointerEvents: "none" }}>
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
          animate={{ x: p.x, y: p.y + 160, rotate: p.r, opacity: [1, 1, 0] }}
          transition={{ duration: 1.3 * p.d, ease: "easeOut" }}
          style={{
            width: p.s,
            height: p.s,
            borderRadius: 2,
            background:
              ["#60A5FA", "#34D399", "#F472B6", "#FBBF24", "#A78BFA"][p.id % 5],
          }}
        />
      ))}
    </div>
  );
};

// Toast for CRUD/events
const Toast = ({ type = "success", text, onDone }) => {
  const colors =
    type === "success"
      ? ["#10B981", "#34D399"]
      : type === "update"
      ? ["#3B82F6", "#60A5FA"]
      : type === "delete"
      ? ["#EF4444", "#F87171"]
      : ["#6366F1", "#A78BFA"]; // default/info
  const icon =
    type === "success" ? "✓" : type === "update" ? "✎" : type === "delete" ? "✖" : "ℹ";

  return (
    <motion.div
      initial={{ y: -30, opacity: 0, scale: 0.98 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      exit={{ y: -30, opacity: 0, scale: 0.98 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      onAnimationComplete={() => setTimeout(onDone, 1400)}
      style={{
        position: "fixed",
        top: 18,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 10000,
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          borderRadius: 12,
          color: "white",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 10,
          boxShadow: "0 10px 30px rgba(0,0,0,.15)",
          background: `linear-gradient(90deg, ${colors[0]}, ${colors[1]})`,
        }}
      >
        <span
          style={{
            width: 26,
            height: 26,
            display: "grid",
            placeItems: "center",
            background: "rgba(255,255,255,.25)",
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          {icon}
        </span>
        <span>{text}</span>
      </div>
    </motion.div>
  );
};

/* ---------- Component ---------- */
const BankAccountDetails = () => {
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [search, setSearch] = useState("");

  // Animation flags
  const [confetti, setConfetti] = useState(false);
  const [toast, setToast] = useState(null); // {type, text}
  const [rowGlowId, setRowGlowId] = useState(null); // highlight row after actions
  const [confirmDelete, setConfirmDelete] = useState(null); // id

  const [form, setForm] = useState({
    name: "",
    accountHolderName: "",
    mobileNumber: "",
    bankType: "",
    accountDetails: "",
    ifscCode: "",
  });

  const collectionRef = collection(db, "bank-account-details");

  // Fetch records
  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const snap = await getDocs(collectionRef);
        const list = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            createDate: data.createDate ? new Date(data.createDate) : new Date(),
          };
        });
        list.sort((a, b) => new Date(b.createDate) - new Date(a.createDate));
        setRecords(list);
      } catch (e) {
        console.error("Error fetching records:", e);
      }
    };
    fetchRecords();
  }, []);

  /* ---------- Helpers ---------- */
  const copyTextFor = (obj) =>
    [
      `Name: ${obj.name || ""}`,
      `Account Holder Name: ${obj.accountHolderName || ""}`,
      `Mobile: ${obj.mobileNumber || ""}`,
      `Bank: ${obj.bankType || ""}`,
      `Account Number: ${obj.accountDetails || ""}`,
      `IFSC: ${obj.ifscCode || ""}`,
    ].join("\n");

  const fireToast = (type, text, confettiAlso = false) => {
    setToast({ type, text });
    if (confettiAlso) {
      setConfetti(true);
      setTimeout(() => setConfetti(false), 1000);
    }
  };

  /* ---------- Handlers ---------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === "ifscCode") v = value.toUpperCase().trim();
    if (name === "mobileNumber") v = value.replace(/\D/g, "").slice(0, 10);
    setForm((prev) => ({ ...prev, [name]: v }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.accountHolderName.trim()) newErrors.accountHolderName = "Account Holder Name is required";
    if (!form.mobileNumber) newErrors.mobileNumber = "Mobile Number is required";
    else if (!inMobile10Regex.test(form.mobileNumber))
      newErrors.mobileNumber = "Enter valid 10-digit Indian mobile";
    if (!form.bankType) newErrors.bankType = "Bank Type is required";
    if (!form.accountDetails.trim())
      newErrors.accountDetails = "Account Details are required";
    if (!form.ifscCode) newErrors.ifscCode = "IFSC Code is required";
    else if (!ifscRegex.test(form.ifscCode))
      newErrors.ifscCode = "Invalid IFSC (e.g., HDFC0001234)";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setForm({
      name: "",
      accountHolderName: "",
      mobileNumber: "",
      bankType: "",
      accountDetails: "",
      ifscCode: "",
    });
    setErrors({});
    setEditId(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      fireToast("info", "Please fix validation errors");
      return;
    }

    try {
      const nowIso = new Date().toISOString();
      const payload = {
        name: form.name.trim(),
        accountHolderName: form.accountHolderName.trim(),
        mobileNumber: form.mobileNumber.trim(),
        bankType: form.bankType,
        accountDetails: form.accountDetails.trim(),
        ifscCode: form.ifscCode.toUpperCase(),
        verifyStatus: records.find((r) => r.id === editId)?.verifyStatus || "not_verified",
        createDate: nowIso, // internal
      };

      if (editId) {
        const existing = records.find((r) => r.id === editId);
        const finalPayload = {
          ...payload,
          createDate:
            (existing?.createDate instanceof Date
              ? existing.createDate.toISOString()
              : existing?.createDate) || nowIso,
        };
        await updateDoc(doc(db, "bank-account-details", editId), finalPayload);

        setRecords((prev) =>
          prev.map((r) =>
            r.id === editId
              ? { ...r, ...finalPayload, createDate: new Date(finalPayload.createDate) }
              : r
          )
        );

        setRowGlowId(editId);
        fireToast("update", "Details updated", true);
      } else {
        const dref = await addDoc(collectionRef, payload);
        const newRow = { ...payload, id: dref.id, createDate: new Date(payload.createDate) };
        setRecords((prev) => [newRow, ...prev]);

        setRowGlowId(dref.id);
        fireToast("success", "New account added", true);
      }
    } catch (e) {
      console.error("Error saving record:", e);
      fireToast("delete", "Save failed"); // using red style for failure
    }

    resetForm();
    setShowForm(false);
  };

  const handleEdit = (record) => {
    setForm({
      name: record.name || "",
      accountHolderName: record.accountHolderName || "",
      mobileNumber: record.mobileNumber || "",
      bankType: record.bankType || "",
      accountDetails: record.accountDetails || "",
      ifscCode: (record.ifscCode || "").toUpperCase(),
    });
    setEditId(record.id);
    setShowForm(true);
    fireToast("info", "Edit mode");
  };

  const handleCopyCurrent = async () => {
    try {
      await navigator.clipboard.writeText(copyTextFor(form));
      fireToast("info", "Copied current form");
      setRowGlowId(editId || "NEW");
      setTimeout(() => setRowGlowId(null), 900);
    } catch {
      fireToast("delete", "Copy failed");
    }
  };

  const handleCopyRow = async (row) => {
    try {
      await navigator.clipboard.writeText(copyTextFor(row));
      fireToast("info", "Copied row");
      setRowGlowId(row.id);
      setTimeout(() => setRowGlowId(null), 900);
    } catch {
      fireToast("delete", "Copy failed");
    }
  };

  const handleDeleteAsk = (id) => {
    setConfirmDelete(id);
    fireToast("info", "Confirm delete?");
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDelete;
    if (!id) return;
    try {
      setDeletingId(id);
      await deleteDoc(doc(db, "bank-account-details", id));
      setRecords((prev) => prev.filter((r) => r.id !== id));
      fireToast("delete", "Record deleted", true);
    } catch (e) {
      console.error("Error deleting record:", e);
      fireToast("delete", "Delete failed");
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  };

  const handleVerifyStatus = async (recordId, verifyStatus) => {
    try {
      await updateDoc(doc(db, "bank-account-details", recordId), {
        verifyStatus,
        verifyUpdatedAt: new Date().toISOString(),
      });
      setRecords((prev) =>
        prev.map((item) =>
          item.id === recordId
            ? { ...item, verifyStatus, verifyUpdatedAt: new Date().toISOString() }
            : item
        )
      );
      setRowGlowId(recordId);
      fireToast("update", `Account marked as ${verifyStatus === "verified" ? "Verified" : "Not Verified"}`);
    } catch (e) {
      console.error("Error updating verify status:", e);
      fireToast("delete", "Verify status update failed");
    }
  };

  /* ---------- Derived ---------- */
  const q = search.trim().toLowerCase();
  const filteredRecords = useMemo(() => {
    if (!q) return records;
    return records.filter((r) =>
      [r.name, r.accountHolderName, r.mobileNumber, r.bankType, r.accountDetails, r.ifscCode]
        .some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [records, q]);

  const publicLink =
    typeof window !== "undefined" ? `${window.location.origin}/bank-account-entry` : "";

  /* ---------- UI ---------- */
  return (
    <div className="boost-acc-wrapper" style={{ position: "relative" }}>
      <Sparkles />
      <Confetti show={confetti} />
      <AnimatePresence>
        {toast && (
          <Toast
            key={toast.text}
            type={toast.type}
            text={toast.text}
            onDone={() => setToast(null)}
          />
        )}
      </AnimatePresence>

      {/* Title */}
      <motion.h2
        className="title"
        initial={{ backgroundPositionX: "0%" }}
        animate={{ backgroundPositionX: ["0%", "100%", "0%"] }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
        style={{
          backgroundImage:
            "linear-gradient(90deg,#22d3ee,#a78bfa,#f472b6,#fbbf24,#22d3ee)",
          backgroundSize: "200% 100%",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          fontWeight: 800,
          letterSpacing: ".5px",
        }}
      >
        Bank Account Details
      </motion.h2>

      {/* Command Bar */}
      <motion.div
        className="d-flex mb-3"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ gap: 12, alignItems: "center", flexWrap: "wrap" }}
      >
        <motion.button
          whileHover={{ scale: 1.03, y: -1 }}
          whileTap={{ scale: 0.98 }}
          className="btn"
          style={{
            background:
              "linear-gradient(90deg, rgba(59,130,246,.95), rgba(99,102,241,.95))",
            color: "white",
            border: "0",
            padding: "10px 16px",
            borderRadius: 12,
            boxShadow: "0 10px 20px -10px rgba(59,130,246,.55)",
            fontWeight: 700,
            letterSpacing: ".3px",
          }}
          onClick={() => {
            const opening = !showForm;
            if (opening) {
              resetForm();
              fireToast("info", "Add mode");
            } else {
              fireToast("info", "Form closed");
            }
            setShowForm(opening);
          }}
        >
          {showForm ? "Close" : "+ Add Account"}
        </motion.button>

        <div className="flex-grow-1" style={{ minWidth: 260, position: "relative" }}>
          <motion.input
            initial={{ filter: "blur(2px)" }}
            animate={{ filter: "blur(0px)" }}
            transition={{ duration: 0.35 }}
            type="text"
            className="form-control"
            placeholder="Search name, mobile, bank, account, IFSC"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            style={{
              paddingLeft: 42,
              height: 42,
              borderRadius: 12,
              border: "1px solid rgba(99,102,241,.25)",
              boxShadow: "inset 0 1px 3px rgba(0,0,0,.04)",
            }}
          />
          <span
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              opacity: 0.65,
              fontSize: 18,
            }}
          >
            🔎
          </span>
        </div>

        <div
          className="px-3 py-2"
          style={{
            minWidth: 280,
            borderRadius: 12,
            background: "rgba(255,255,255,0.8)",
            border: "1px solid rgba(99,102,241,.15)",
          }}
        >
          <div className="small text-uppercase fw-bold text-muted">Public Link</div>
          <div style={{ wordBreak: "break-all", fontWeight: 600 }}>{publicLink}</div>
        </div>
      </motion.div>

      {/* Modal Form (with Copy in header) */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            className="modal show d-block"
            tabIndex="-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="modal-dialog">
              <motion.div
                className="modal-content form-animation"
                initial={{ scale: 0.95, y: 10, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.97, y: 10, opacity: 0 }}
                transition={{ type: "spring", stiffness: 240, damping: 20 }}
                style={{
                  borderRadius: 16,
                  overflow: "hidden",
                  boxShadow:
                    "0 12px 40px -12px rgba(99,102,241,.3), 0 8px 24px -16px rgba(236,72,153,.25)",
                  border: "1px solid rgba(99,102,241,.15)",
                }}
              >
                <div
                  className="modal-header"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(59,130,246,.08), rgba(236,72,153,.08))",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <h5 className="modal-title" style={{ flex: 1 }}>
                    {editId ? "Edit Details" : "Add Bank Account Details"}
                  </h5>

                  {/* COPY current form button */}
                  <motion.button
                    className="btn btn-sm btn-outline-primary"
                    whileHover={{ scale: 1.07 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleCopyCurrent}
                    title="Copy current form"
                  >
                    Copy
                  </motion.button>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowForm(false);
                      fireToast("info", "Form closed");
                    }}
                  ></button>
                </div>

                <div className="modal-body">
                  <form onSubmit={handleFormSubmit}>
                    {/* Name */}
                    <motion.div
                      className="mb-3"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <label>Name</label>
                      <input
                        type="text"
                        name="name"
                        className={`form-control ${errors.name ? "is-invalid" : ""}`}
                        value={form.name}
                        onChange={handleInputChange}
                      />
                      {errors.name && <div className="text-danger small">{errors.name}</div>}
                    </motion.div>

                    {/* Mobile */}
                    <motion.div
                      className="mb-3"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: 0.04 }}
                    >
                      <label>Account Holder Name</label>
                      <input
                        type="text"
                        name="accountHolderName"
                        className={`form-control ${errors.accountHolderName ? "is-invalid" : ""}`}
                        value={form.accountHolderName}
                        onChange={handleInputChange}
                      />
                      {errors.accountHolderName && (
                        <div className="text-danger small">{errors.accountHolderName}</div>
                      )}
                    </motion.div>

                    <motion.div
                      className="mb-3"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: 0.06 }}
                    >
                      <label>Mobile Number</label>
                      <input
                        type="tel"
                        name="mobileNumber"
                        inputMode="numeric"
                        className={`form-control ${errors.mobileNumber ? "is-invalid" : ""}`}
                        value={form.mobileNumber}
                        onChange={handleInputChange}
                        placeholder="10-digit Indian mobile"
                      />
                      {errors.mobileNumber && (
                        <div className="text-danger small">{errors.mobileNumber}</div>
                      )}
                    </motion.div>

                    {/* Bank Type */}
                    <motion.div
                      className="mb-3"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: 0.1 }}
                    >
                      <label>Bank Type</label>
                      <select
                        name="bankType"
                        className={`form-control ${errors.bankType ? "is-invalid" : ""}`}
                        value={form.bankType}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Bank</option>
                        {INDIAN_BANKS.map((b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ))}
                      </select>
                      {errors.bankType && <div className="text-danger small">{errors.bankType}</div>}
                    </motion.div>

                    {/* Account Details */}
                    <motion.div
                      className="mb-3"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: 0.14 }}
                    >
                      <label>Account Number</label>
                      <input
                        type="text"
                        name="accountDetails"
                        className={`form-control ${errors.accountDetails ? "is-invalid" : ""}`}
                        value={form.accountDetails}
                        onChange={handleInputChange}
                        placeholder="Enter account number"
                      />
                      {errors.accountDetails && (
                        <div className="text-danger small">{errors.accountDetails}</div>
                      )}
                    </motion.div>

                    {/* IFSC */}
                    <motion.div
                      className="mb-3"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: 0.18 }}
                    >
                      <label>IFSC Code</label>
                      <input
                        type="text"
                        name="ifscCode"
                        className={`form-control ${errors.ifscCode ? "is-invalid" : ""}`}
                        value={form.ifscCode}
                        onChange={handleInputChange}
                        placeholder="e.g., HDFC0001234"
                      />
                      {errors.ifscCode && <div className="text-danger small">{errors.ifscCode}</div>}
                    </motion.div>

                    <motion.button
                      type="submit"
                      className="btn w-100"
                      whileHover={{ scale: 1.015, y: -1 }}
                      whileTap={{ scale: 0.985 }}
                      style={{
                        background:
                          "linear-gradient(90deg, rgba(16,185,129,.95), rgba(5,150,105,.95))",
                        color: "white",
                        border: 0,
                        borderRadius: 12,
                        height: 44,
                        fontWeight: 700,
                        letterSpacing: ".3px",
                        boxShadow: "0 10px 22px -12px rgba(16,185,129,.55)",
                      }}
                    >
                      {editId ? "Update" : "Submit"}
                    </motion.button>
                  </form>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirm */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            key="confirm"
            className="modal show d-block"
            tabIndex="-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="modal-dialog">
              <motion.div
                className="modal-content"
                initial={{ scale: 0.95, y: 10, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.97, y: 10, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                style={{ borderRadius: 14 }}
              >
                <div className="modal-header">
                  <h5 className="modal-title">Delete record?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setConfirmDelete(null);
                      fireToast("info", "Delete cancelled");
                    }}
                  ></button>
                </div>
                <div className="modal-body">This action cannot be undone.</div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setConfirmDelete(null);
                      fireToast("info", "Delete cancelled");
                    }}
                  >
                    Cancel
                  </button>
                  <motion.button
                    className="btn btn-danger"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleDeleteConfirm}
                  >
                    Delete
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      <div className="table-container mt-3">
        <div className="table-scroll">
          <table className="table table-striped custom-table">
            <thead>
              <tr>
                <th style={{ width: 70 }}>S.No</th>
                <th>Name</th>
                <th>Account Holder</th>
                <th>Mobile</th>
                <th>Bank</th>
                <th>Account Number</th>
                <th>IFSC</th>
                <th>Verify Status</th>
                <th style={{ width: 290 }}>Actions</th>
              </tr>
            </thead>
            <motion.tbody layout>
              <AnimatePresence initial={false}>
                {filteredRecords.map((r, idx) => {
                  const glow = rowGlowId === r.id;
                  return (
                    <motion.tr
                      key={r.id}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        boxShadow: glow ? "0 0 0 0 rgba(16,185,129,0)" : "none",
                      }}
                      exit={{
                        opacity: 0,
                        y: -8,
                        backgroundColor: "rgba(239,68,68,.08)",
                      }}
                      transition={{ duration: 0.2 }}
                      onAnimationComplete={() => {
                        if (glow) setTimeout(() => setRowGlowId(null), 900);
                      }}
                      style={{
                        position: "relative",
                        background: glow ? "rgba(16,185,129,.07)" : "transparent",
                      }}
                    >
                      {/* Glow ring */}
                      {glow && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.98 }}
                          animate={{ opacity: [0.7, 0], scale: [1, 1.05] }}
                          transition={{ duration: 0.9 }}
                          style={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: 10,
                            border: "2px solid rgba(16,185,129,.45)",
                            pointerEvents: "none",
                          }}
                        />
                      )}

                      <td>{idx + 1}</td>
                      <td>{r.name.toUpperCase()}</td>
                      <td>{(r.accountHolderName || "-").toUpperCase()}</td>
                      <td>{r.mobileNumber}</td>
                      <td>{r.bankType.toUpperCase()}</td>
                      <td>{r.accountDetails}</td>
                      <td>{r.ifscCode}</td>
                      <td>
                        <span className={`badge ${r.verifyStatus === "verified" ? "text-bg-success" : "text-bg-secondary"}`}>
                          {r.verifyStatus === "verified" ? "Verified" : "Not Verified"}
                        </span>
                      </td>
                      <td>
                        {deletingId === r.id ? (
                          <motion.span
                            className="text-danger fw-bold"
                            initial={{ opacity: 0.6 }}
                            animate={{ opacity: [0.6, 0.2, 0.6] }}
                            transition={{ repeat: Infinity, duration: 0.9 }}
                          >
                            Deleting…
                          </motion.span>
                        ) : (
                          <div className="d-flex" style={{ gap: 8 }}>
                            <motion.button
                              className="btn btn-sm btn-success"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.97 }}
                              onClick={() => handleVerifyStatus(r.id, "verified")}
                            >
                              Verify
                            </motion.button>
                            <motion.button
                              className="btn btn-sm btn-outline-dark"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.97 }}
                              onClick={() => handleVerifyStatus(r.id, "not_verified")}
                            >
                              Not Verify
                            </motion.button>
                            <motion.button
                              className="btn btn-sm btn-outline-secondary"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.97 }}
                              onClick={() => handleCopyRow(r)}
                            >
                              Copy
                            </motion.button>
                            <motion.button
                              className="btn btn-sm btn-warning"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.97 }}
                              onClick={() => handleEdit(r)}
                            >
                              Edit
                            </motion.button>
                            <motion.button
                              className="btn btn-sm btn-danger"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.97 }}
                              onClick={() => handleDeleteAsk(r.id)}
                            >
                              Delete
                            </motion.button>
                          </div>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
                {filteredRecords.length === 0 && (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 0.9 }} className="text-center">
                    <td colSpan={9} className="py-4">
                      No records found.
                    </td>
                  </motion.tr>
                )}
              </AnimatePresence>
            </motion.tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BankAccountDetails;
